import { type isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import { isolationContextId } from '@atlaskit/atlassian-context/isolation-context-id';

/**
 * Slug of Admin Hub's app environment block in Cosmos
 * https://bitbucket.org/atlassian/cosmos/src/main/central-domain-config/aem-encoding/environment-blocks/app-environment-blocks/adminhub/adminhub.yml
 */
export const ADMINHUB = 'adminhub';

/**
 * Every feature key AEM recognizes for Admin Hub's environment block, read off the
 * SDK's own signature so a key that is renamed or dropped in Cosmos fails to compile
 * here rather than silently resolving to `false` at runtime.
 */
export type AdminHubFeatureKey = Parameters<typeof isFeatureEnabled<typeof ADMINHUB>>[1];

/**
 * Feature keys declared in AEM (Atlassian Environment Model) feature registry via Cosmos for Admin Hub
 *
 * Source of truth (edit here to update or add offering exclusions for a feature key):
 *   https://bitbucket.org/atlassian/cosmos/src/main/central-domain-config/aem-encoding/environment-blocks/app-environment-blocks/adminhub/features/
 *
 * Environment parity reference:
 *   https://developer.atlassian.com/platform/isolation-platform/FeatureParity/feature-environment-block-parity/#feature-parity
 */
export const ADMINHUB_ACCESS_REQUESTS = 'adminhub-access-requests';
export const ADMINHUB_ADD_PRODUCT = 'adminhub-add-product';
export const ADMINHUB_ADMIN_INSIGHTS = 'adminhub-admin-insights';
export const ADMINHUB_AI_ENABLED_APPS = 'adminhub-ai-enabled-apps';
export const ADMINHUB_API_TOKEN_CONTROLS = 'adminhub-api-token-controls';
export const ADMINHUB_APP_REQUESTS = 'adminhub-app-requests';
export const ADMINHUB_APP_UPDATES = 'adminhub-app-updates';
export const ADMINHUB_APPLICATION_TUNNELS = 'adminhub-application-tunnels';
export const ADMINHUB_ATLASSIAN_INTELLIGENCE = 'adminhub-atlassian-intelligence';
export const ADMINHUB_AUDIT_LOG = 'adminhub-audit-log';
export const ADMINHUB_AUTHENTICATION_POLICIES = 'adminhub-authentication-policies';
export const ADMINHUB_AUTOMATIONS = 'adminhub-automations';
export const ADMINHUB_BACKUP_AND_RESTORE = 'adminhub-backup-and-restore';
export const ADMINHUB_BILLING_CONSOLE = 'adminhub-billing-console';
export const ADMINHUB_BILLING_PREVIEW = 'adminhub-billing-preview';
export const ADMINHUB_CODE_INTELLIGENCE = 'adminhub-code-intelligence';
export const ADMINHUB_CONNECTED_EMOJI = 'adminhub-connected-emoji';
export const ADMINHUB_CONNECTED_SOURCES = 'adminhub-connected-sources';
export const ADMINHUB_CONNECTOR_PROTECTION = 'adminhub-connector-protection';
export const ADMINHUB_CONTACTS = 'adminhub-contacts';
export const ADMINHUB_CUSTOM_DOMAIN_EMAIL_NOTIFICATION =
	'adminhub-custom-domain-email-notification';
export const ADMINHUB_CUSTOM_DOMAINS = 'adminhub-custom-domains';
export const ADMINHUB_DATA_MANAGEMENT = 'adminhub-data-management';
export const ADMINHUB_DATA_RESIDENCY = 'adminhub-data-residency';
export const ADMINHUB_DATA_TRANSFER = 'adminhub-data-transfer';
export const ADMINHUB_DELTA_MIGRATIONS = 'adminhub-delta-migrations';
export const ADMINHUB_DISASTER_RECOVERY = 'adminhub-disaster-recovery';
export const ADMINHUB_DISCOVER_APPS_SHADOW_IT = 'adminhub-discover-apps-shadow-it';
export const ADMINHUB_DISCOVER_NEW_PRODUCTS = 'adminhub-discover-new-products';
export const ADMINHUB_ENABLE_USER_MERGE = 'adminhub-enable-user-merge';
export const ADMINHUB_ENCRYPTION = 'adminhub-encryption';
export const ADMINHUB_ENVIRONMENTS = 'adminhub-environments';
export const ADMINHUB_EXPERIMENT_CONTROLS = 'adminhub-experiment-controls';
export const ADMINHUB_EXTERNAL_USER_SECURITY = 'adminhub-external-user-security';
export const ADMINHUB_FOOTPRINT = 'adminhub-footprint';
export const ADMINHUB_FREEZE_WINDOWS = 'adminhub-freeze-windows';
export const ADMINHUB_HELP = 'adminhub-help';
export const ADMINHUB_HIPAA_COMPLIANCE = 'adminhub-hippa-compliance';
export const ADMINHUB_JSM_SCIM = 'adminhub-jsm-scim';
export const ADMINHUB_LINK_BITBUCKET = 'adminhub-link-bitbucket';
export const ADMINHUB_LLM = 'adminhub-llm';
export const ADMINHUB_LOCAL_TRANSCRIPT_UPLOAD = 'adminhub-local-transcript-upload';
export const ADMINHUB_LOGIN_CUSTOMISATION = 'adminhub-login-customisation';
export const ADMINHUB_MANAGED_ACCOUNTS_EXPORT = 'adminhub-managed-accounts-export';
export const ADMINHUB_MARKETPLACE_APPS = 'adminhub-marketplace-apps';
export const ADMINHUB_MCP_SETTINGS = 'adminhub-mcp-settings';
export const ADMINHUB_MOBILE_APP_POLICY = 'adminhub-mobile-app-policy';
export const ADMINHUB_NESTED_SIDE_NAVIGATION = 'adminhub-nested-side-navigation';
export const ADMINHUB_ORG_CREATE = 'adminhub-org-create';
export const ADMINHUB_ORG_DELETION = 'adminhub-org-deletion';
export const ADMINHUB_PERFORMANCE_MONITORING = 'adminhub-performance-monitoring';
export const ADMINHUB_PLATFORM_USAGE = 'adminhub-platform-usage';
export const ADMINHUB_POLICIES = 'adminhub-policies';
export const ADMINHUB_PRODUCT_INSIGHTS = 'adminhub-product-insights';
export const ADMINHUB_PRODUCT_LINKS = 'adminhub-product-links';
export const ADMINHUB_PRODUCT_RECOMMENDATION = 'adminhub-product-recommendation';
export const ADMINHUB_PRODUCT_URLS = 'adminhub-product-urls';
export const ADMINHUB_RELEASE_TRACKS = 'adminhub-release-tracks';
export const ADMINHUB_REVAMPED_ORGANIZATION_OVERVIEW = 'adminhub-revamped-organization-overview';
export const ADMINHUB_ROVO_ACCESS = 'adminhub-rovo-access';
export const ADMINHUB_ROVO_CHAT_PROTECTION = 'adminhub-rovo-chat-protection';
export const ADMINHUB_ROVO_INSIGHTS = 'adminhub-rovo-insights';
export const ADMINHUB_ROVO_MCP_SERVER = 'adminhub-rovo-mcp-server';
export const ADMINHUB_ROVO_SETTINGS = 'adminhub-rovo-settings';
export const ADMINHUB_SANDBOX = 'adminhub-sandbox';
export const ADMINHUB_SECURITY_CHECKLIST = 'adminhub-security-checklist';
export const ADMINHUB_SIGNUP = 'adminhub-signup';
export const ADMINHUB_SITE_RENAME = 'adminhub-site-rename';
export const ADMINHUB_SITE_TRANSFER = 'adminhub-site-transfer';
export const ADMINHUB_SYSTEM_HEALTH = 'adminhub-system-health';
export const ADMINHUB_TEAM_SITE_ASSIGNMENT = 'adminhub-team-site-assignment';
export const ADMINHUB_TEAMS = 'adminhub-teams';
export const ADMINHUB_TEAMWORK_GRAPH_CLI = 'adminhub-teamwork-graph-cli';
export const ADMINHUB_UNITS = 'adminhub-units';
export const ADMINHUB_USER_ACCESS_INVITES = 'adminhub-user-access-invites';
export const ADMINHUB_USER_ACCESS_SETTINGS = 'adminhub-user-access-settings';
export const ADMINHUB_USER_CONNECTED_APPS = 'adminhub-user-connected-apps';
export const ADMINHUB_USER_COUNTS = 'adminhub-user-counts';
export const ADMINHUB_USER_LAST_ACTIVE_TIMESTAMP = 'adminhub-user-last-active-timestamp';
export const ADMINHUB_WORKSPACES_TABLE_PLANS = 'adminhub-workspaces-table-plans';

/**
 * Atlassian Environment Model (AEM) cannot target releases exclusively at pre-prod IC
 * This is a check to enable feature testing in pre-prod IC
 */
export function isPreProdIsolatedCloud(): boolean {
	return isolationContextId() === 'ic-dr6';
}
